import PropTypes from "prop-types";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import taskService from "../services/taskService";

const TaskContext = createContext();

export function TaskProvider({ children }) {
  const [tasks, setTasks] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
    page: 1,
    page_size: 20,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchTasks = useCallback(async (filters = {}) => {
    setLoading(true);
    setError(null);
    try {
      const response = await taskService.listTasks(filters);
      const results = response.results || response.tasks || [];
      setTasks(results);
      setPagination({
        count: response.count || results.length,
        next: response.next || null,
        previous: response.previous || null,
        page: Number(filters.page || 1),
        page_size: Number(filters.page_size || 20),
      });
      return response;
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load tasks");
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTaskById = useCallback(async (taskId) => {
    setLoading(true);
    setError(null);
    try {
      const task = await taskService.getTask(taskId);
      setSelectedTask(task);
      return task;
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load task");
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const createTask = useCallback(async (data) => {
    setLoading(true);
    setError(null);
    try {
      const created = await taskService.createTask(data);
      setTasks((current) => [created, ...current]);
      setSelectedTask(created);
      return created;
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to create task");
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const bulkCreateTasks = useCallback(async (data) => {
    setLoading(true);
    setError(null);
    try {
      const response = await taskService.bulkCreateTasks(data);
      const created = response.created || [];
      setTasks((current) => [...created, ...current]);
      return response;
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to create tasks");
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateTask = useCallback(async (taskId, data) => {
    setLoading(true);
    setError(null);
    try {
      const updated = await taskService.updateTask(taskId, data);
      setTasks((current) =>
        current.map((item) => (item.task_id === taskId ? updated : item)),
      );
      setSelectedTask(updated);
      return updated;
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to update task");
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteTask = useCallback(
    async (taskId) => {
      setLoading(true);
      setError(null);
      try {
        await taskService.deleteTask(taskId);
        setTasks((current) => current.filter((item) => item.task_id !== taskId));
        if (selectedTask?.task_id === taskId) {
          setSelectedTask(null);
        }
        return true;
      } catch (err) {
        setError(err.response?.data?.detail || "Failed to delete task");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [selectedTask?.task_id],
  );

  const fetchPropertyTasks = useCallback(async (propertyId, filters = {}) => {
    return taskService.listPropertyTasks(propertyId, filters);
  }, []);

  const value = useMemo(
    () => ({
      tasks,
      selectedTask,
      pagination,
      loading,
      error,
      fetchTasks,
      fetchTaskById,
      createTask,
      bulkCreateTasks,
      updateTask,
      deleteTask,
      fetchPropertyTasks,
    }),
    [
      tasks,
      selectedTask,
      pagination,
      loading,
      error,
      fetchTasks,
      fetchTaskById,
      createTask,
      bulkCreateTasks,
      updateTask,
      deleteTask,
      fetchPropertyTasks,
    ],
  );

  return <TaskContext.Provider value={value}>{children}</TaskContext.Provider>;
}

TaskProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useTaskContext = () => {
  const context = useContext(TaskContext);

  if (!context) {
    throw new Error("useTaskContext must be used within TaskProvider");
  }

  return context;
};

export default TaskContext;